/** @format */
import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
    CROSS_STUDY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT,
    OPEN_UNIVERSITY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT,
} from 'common-typescript/constants';
import { UniversitySettings } from 'common-typescript/types';
import { take } from 'rxjs';
import { AppErrorHandler, NavigationTab, TabNavigationComponent, UniversityService } from 'sis-components';

export const CourseUnitInfoTabIds = {
    BROCHURE: 'course-unit-info-brochure-tab',
    COMPLETION_METHODS: 'course-unit-info-completion-methods-tab',
    SUBSTITUTIONS: 'course-unit-info-substitutions-tab',
    OPEN_UNIVERSITY_OFFERING: 'course-unit-info-open-university-offering-tab',
    CROSS_STUDY: 'course-unit-info-cross-study-tab',
};
@Component({
    selector: 'app-course-unit-info-navigation',
    templateUrl: './course-unit-info-navigation.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class CourseUnitInfoNavigationComponent implements OnInit {
    readonly defaultTabs: NavigationTab[] = [
        {
            path: 'student.course-unit.brochure',
            controls: CourseUnitInfoTabIds.BROCHURE,
            id: CourseUnitInfoTabIds.BROCHURE,
            title: 'COURSE_UNIT_INFO_MODAL.TAB_HEADINGS.INFO',
        },
        {
            path: 'student.course-unit.completion-methods',
            controls: CourseUnitInfoTabIds.COMPLETION_METHODS,
            id: CourseUnitInfoTabIds.COMPLETION_METHODS,
            title: 'COURSE_UNIT_INFO_MODAL.TAB_HEADINGS.COMPLETION_METHODS',
        },
        {
            path: 'student.course-unit.substitutions',
            controls: CourseUnitInfoTabIds.SUBSTITUTIONS,
            id: CourseUnitInfoTabIds.SUBSTITUTIONS,
            title: 'COURSE_UNIT_INFO_MODAL.TAB_HEADINGS.SUBSTITUTIONS',
        },
    ];

    @ViewChild('tabNavigation') private tabNavigation: TabNavigationComponent;

    tabs: NavigationTab[] = [];

    constructor(
        private ref: ChangeDetectorRef,
        private universityService: UniversityService,
        private appErrorHandler: AppErrorHandler,
    ) {}

    ngOnInit() {
        this.universityService
            .getCurrentUniversitySettings()
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe((settings: UniversitySettings) => {
                const showOpenUniversityTab = this.openUniversityFeaturesEnabledForStudent(settings);
                const showCrossStudyTab = this.crossStudyFeaturesEnabledForStudent(settings);
                this.setTabs(showOpenUniversityTab, showCrossStudyTab);
                // This prevents an NG0100 error due to changes being made outside a change detection cycle
                this.ref.detectChanges();
            });
    }

    get selectedTab(): NavigationTab | null {
        return this.tabNavigation?.selected ?? null;
    }

    setTabs(showOpenUniversityTab: boolean, showCrossStudyTab: boolean) {
        // show open university, cross study and completion methods 2.0 tabs if these features are enabled for student in current university settings
        const tabsWithInsertions = [...this.defaultTabs];
        this.tabs = [
            ...tabsWithInsertions,
            ...(showOpenUniversityTab
                ? [
                      {
                          path: 'student.course-unit.open-university',
                          title: 'COURSE_UNIT_INFO_MODAL.TAB_HEADINGS.OPEN_UNIVERSITY',
                          controls: CourseUnitInfoTabIds.OPEN_UNIVERSITY_OFFERING,
                          id: CourseUnitInfoTabIds.OPEN_UNIVERSITY_OFFERING,
                      },
                  ]
                : []),
            ...(showCrossStudyTab
                ? [
                      {
                          path: 'student.course-unit.cross-study',
                          title: 'STUDIES.CROSS_STUDY.TITLE',
                          controls: CourseUnitInfoTabIds.CROSS_STUDY,
                          id: CourseUnitInfoTabIds.CROSS_STUDY,
                      },
                  ]
                : []),
        ];
    }

    openUniversityFeaturesEnabledForStudent(settings: UniversitySettings) {
        return (
            settings?.frontendFeatureToggles?.openUniversityFeaturesEnabledForStudent ??
            OPEN_UNIVERSITY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT
        );
    }

    crossStudyFeaturesEnabledForStudent(settings: UniversitySettings) {
        return settings?.frontendFeatureToggles?.crossStudyFeaturesEnabledForStudent ?? CROSS_STUDY_FEATURES_ENABLED_FOR_STUDENT_DEFAULT;
    }
}
